import { useSelector } from 'react-redux';
import { Badge, Spacer } from '@vinted/web-ui';
import { getUser } from 'state/session/selectors';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { getShowFundraiser, getIsReferralsEnabled } from 'state/selectors';
import { getUserThumbnail } from 'data/utils/user';
import { logoutUser } from 'data/api/account';
import useTranslate from 'hooks/useTranslate';
import { DONATIONS_SETTINGS_URL, GO_TO_WALLET_URL, MEMBER_PROFILE_URL, PROFILE_SETTINGS_URL, REFERRALS_URL, ROOT_URL, USER_PERSONALIZATION_SIZES_URL, WALLET_INVOICES_URL, MY_ORDERS_URL } from 'constants/routes';
import useTracking from 'hooks/useTracking';
import { donationsClickEvent } from 'libs/common/event-tracker/events';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { navigateToPage } from 'libs/utils/window';
import useShouldShowDarkModeAdoption from 'hooks/useShouldShowDarkModeBanner';
import { OrdersType } from 'constants/my-orders';
import CurrentWalletAmount from './CurrentWalletAmount';
import UserMenuDropdown from './UserMenuDropdown';
import UserMenuGroup from './UserMenuGroup';
import UserMenuInjected from './UserMenuInjected';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

const UserMenu = _ref => {
  let {
    injectedMenus
  } = _ref;
  const user = useSelector(getUser);
  const translate = useTranslate();
  const tracker = useTracking();
  const isPaymentsEnabled = useSelector(getIsFeatureSwitchEnabled('payments'));
  const isMyOrdersNavAndFiltersEnabled = useSelector(getIsFeatureSwitchEnabled('my_orders_nav_and_filters'));
  const isMyOrdersEnabled = useSelector(getIsFeatureSwitchEnabled('next_page_my_orders'));
  const showFundraiser = useSelector(getShowFundraiser);
  const isReferralsEnabled = useSelector(getIsReferralsEnabled);
  const shouldShowDarkModeAdoption = useShouldShowDarkModeAdoption();
  if (!user) return null;
  const userPhoto = getUserThumbnail(user.photo);

  const handleLogoutClick = async () => {
    const response = await logoutUser();
    if (response) navigateToPage(ROOT_URL);
  };

  const handleFundraiserClick = () => tracker.track(donationsClickEvent({
    target: ClickableElement.EnterDonationsFromWebMenu
  }));

  const renderWalletAction = () => /*#__PURE__*/_jsx(UserMenuGroup.Action, {
    href: GO_TO_WALLET_URL,
    children: /*#__PURE__*/_jsxs("span", {
      className: "u-flexbox u-justify-content-between u-align-items-center",
      children: [translate('header.user_menu.account.items.wallet.title'), /*#__PURE__*/_jsx("span", {
        className: "u-ui-padding-left-regular",
        children: /*#__PURE__*/_jsx(CurrentWalletAmount, {})
      })]
    })
  });

  const renderMyOrdersAction = () => /*#__PURE__*/_jsx(UserMenuGroup.Action, {
    href: MY_ORDERS_URL({
      type: isMyOrdersNavAndFiltersEnabled ? OrdersType.Sold : OrdersType.All
    }),
    children: translate('header.user_menu.account.items.my_orders.title')
  });

  const renderFundraiserAction = () => /*#__PURE__*/_jsx(UserMenuGroup.Action, {
    href: DONATIONS_SETTINGS_URL,
    onClick: handleFundraiserClick,
    children: translate('header.user_menu.account.items.donations.title')
  });

  const renderLogoutAction = () => /*#__PURE__*/_jsx(UserMenuGroup.Action, {
    onClick: handleLogoutClick,
    children: /*#__PURE__*/_jsx("span", {
      className: "u-color-red",
      children: translate('header.user_menu.account.items.logout.title')
    })
  });

  return /*#__PURE__*/_jsxs(UserMenuDropdown, {
    userName: user.login,
    userPhoto: userPhoto,
    children: [/*#__PURE__*/_jsx(UserMenuInjected, {
      injectedMenus: injectedMenus
    }), /*#__PURE__*/_jsx(UserMenuGroup, {
      children: /*#__PURE__*/_jsxs(UserMenuGroup.Item, {
        title: translate('header.user_menu.account.title'),
        children: [/*#__PURE__*/_jsx(UserMenuGroup.Action, {
          href: MEMBER_PROFILE_URL(user.id),
          children: translate('header.user_menu.account.items.profile.title')
        }), /*#__PURE__*/_jsxs(UserMenuGroup.Action, {
          href: PROFILE_SETTINGS_URL,
          children: [translate('header.user_menu.account.items.settings.title'), shouldShowDarkModeAdoption && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Spacer, {
              orientation: Spacer.Orientation.Vertical,
              size: Spacer.Size.Small
            }), /*#__PURE__*/_jsx(Badge, {
              theme: "primary",
              content: translate('common.new_badge')
            })]
          })]
        }), /*#__PURE__*/_jsx(UserMenuGroup.Action, {
          href: USER_PERSONALIZATION_SIZES_URL,
          children: translate('header.user_menu.account.items.personalization.title')
        }), isPaymentsEnabled && renderWalletAction(), isMyOrdersEnabled && renderMyOrdersAction(), showFundraiser && renderFundraiserAction(), !isPaymentsEnabled && /*#__PURE__*/_jsx(UserMenuGroup.Action, {
          href: WALLET_INVOICES_URL,
          children: translate('header.user_menu.account.items.invoices.title')
        }), isReferralsEnabled && /*#__PURE__*/_jsx(UserMenuGroup.Action, {
          href: REFERRALS_URL,
          children: translate('header.user_menu.account.items.referrals.title')
        }), renderLogoutAction()]
      })
    })]
  });
};

export default UserMenu;